
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3gn058oo35phc13loh0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 via-amber-50/20 to-gray-50 relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-100/10 via-transparent to-yellow-100/10"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-amber-200/5 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-yellow-200/5 to-transparent rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-gray-900">Contact &amp;</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-700 ml-2">Offices</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 mx-auto mb-8 shadow-sm"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to accelerate your growth? Get in touch with our 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600 font-semibold"> prestigious team</span> today.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold mb-8">
              <span className="text-gray-900">Get in</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600 ml-2">Touch</span>
            </h3>
            
            <div className="space-y-6 mb-8">
              {/* Nairobi Office */}
              <div className="flex items-start group">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600">Nairobi</span> Office
                  </h4>
                  <p className="text-gray-600">Chester House, 2nd Floor<br />Nairobi, Kenya</p>
                </div>
              </div>

              {/* Chicago Office */}
              <div className="flex items-start group">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600">Chicago</span> Office
                  </h4>
                  <p className="text-gray-600">111 W Monroe St, 12th Floor<br />Chicago, USA</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start group">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600">Premium</span> Email
                  </h4>
                  <a 
                    href="mailto:info@castleandcastle.com" 
                    className="text-amber-600 hover:text-amber-700 cursor-pointer font-medium transition-colors"
                  >
                    info@castleandcastle.com
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className="flex items-start group">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-9 0 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <i className="ri-twitter-x-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600">Elite</span> Social Media
                  </h4>
                  <a 
                    href="https://x.com/CastleCastleHQ" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-amber-600 hover:text-amber-700 cursor-pointer font-medium transition-colors"
                  >
                    @CastleCastleHQ
                  </a>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-br from-white via-amber-50/30 to-white rounded-xl p-6 shadow-lg border border-amber-100/50 hover:shadow-xl transition-all duration-300">
              <h4 className="font-bold text-gray-900 mb-4">
                Ready to 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600 ml-1">Get Started?</span>
              </h4>
              <p className="text-gray-600 mb-4">
                Schedule a free consultation to discuss your marketing goals and discover how our 
                <span className="text-amber-700 font-medium"> prestigious expertise</span> can elevate your brand.
              </p>
              <Button onClick={scrollToContact} variant="secondary" className="w-full">
                <i className="ri-calendar-line mr-2"></i>
                Schedule Free Consultation
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-white via-amber-50/20 to-white rounded-xl p-8 shadow-lg border border-amber-100/50 hover:shadow-xl transition-all duration-300">
            <h3 className="text-2xl font-bold mb-6">
              <span className="text-gray-900">Send us a</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600 ml-2">Message</span>
            </h3>
            
            <form data-readdy-form id="contact-form" onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-770 mb-2">
                    Full Name <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-amber-200/50 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-gradient-to-r from-white to-amber-50/20 hover:border-amber-300/50 transition-colors"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-770 mb-2">
                    Email Address <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-amber-200/50 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-gradient-to-r from-white to-amber-50/20 hover:border-amber-300/50 transition-colors"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-770 mb-2">
                    Message <span className="text-amber-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-amber-200/50 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none bg-gradient-to-r from-white to-amber-50/20 hover:border-amber-300/50 transition-colors"
                    placeholder="Tell us about your project and goals..."
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 font-medium">
                      <i className="ri-check-line mr-2"></i>
                      Message sent successfully! We'll get back to you soon.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 font-medium">
                      <i className="ri-error-warning-line mr-2"></i>
                      Failed to send message. Please try again.
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  variant="primary" 
                  className="w-full bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 shadow-lg hover:shadow-xl"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
